/**
 * Render Coordinator - Deterministic render frame scheduling and tracking.
 *
 * Coordinates the rendering lifecycle between the page manager and the
 * component renderers. Responsibilities:
 *
 *   - Schedule render frames for a given page (debounced, priority-ordered)
 *   - Track frame state (pending, running, completed, failed)
 *   - Emit render lifecycle events for observability
 *   - Enforce a maximum concurrent frame limit to prevent runaway renders
 *   - Provide frame statistics for diagnostics and the DevTools inspector
 *
 * The coordinator does NOT perform rendering itself. It calls back through
 * a registered RenderDriver, which is implemented by the UI layer
 * (RuntimeRenderer.tsx or similar). This keeps the engine pure TypeScript
 * with no React dependency.
 *
 * Invariants:
 *   - Only one frame may be active per pageId at any time
 *   - A new request for an active page cancels the pending one (coalescing)
 *   - All frames timeout after MAX_FRAME_MS and emit a failed event
 *   - Frame IDs are stable UUIDs for correlation across subsystems
 */

import { logger }      from "@/system/logger";
import { eventBus }    from "@/runtime/eventBus";

// ── Types ─────────────────────────────────────────────────────────────────────

export type FrameStatus = "pending" | "running" | "completed" | "failed" | "cancelled";

export interface RenderFrame {
  frameId:     string;
  pageId:      string;
  requestedAt: number;
  startedAt?:  number;
  completedAt?: number;
  status:      FrameStatus;
  error?:      string;
  durationMs?: number;
}

export type RenderDriver = (frame: RenderFrame) => void | Promise<void>;

export interface RenderCoordinatorStats {
  totalFrames:      number;
  completedFrames:  number;
  failedFrames:     number;
  cancelledFrames:  number;
  activeFrames:     number;
  avgDurationMs:    number;
  isReady:          boolean;
}

// ── Configuration ─────────────────────────────────────────────────────────────

const MAX_FRAME_MS      = 5_000;
const MAX_CONCURRENT    = 4;
const DEBOUNCE_MS       = 16;   // ~1 rAF interval
const MAX_HISTORY       = 500;

// ── Coordinator ───────────────────────────────────────────────────────────────

class RenderCoordinatorImpl {
  private driver:   RenderDriver | null = null;
  private frames    = new Map<string, RenderFrame>();  // frameId -> frame
  private active    = new Map<string, string>();        // pageId -> frameId
  private history:  RenderFrame[] = [];
  private timers    = new Map<string, ReturnType<typeof setTimeout>>();
  private _ready    = false;

  private _total     = 0;
  private _completed = 0;
  private _failed    = 0;
  private _cancelled = 0;
  private _durationsMs: number[] = [];

  // ── Setup ────────────────────────────────────────────────────────────────────

  /**
   * Register the render driver. Must be called before any frames are requested.
   * The driver is called once per frame with the frame descriptor.
   * Returns the previous driver (or null) so the caller can restore it.
   */
  setDriver(driver: RenderDriver): RenderDriver | null {
    const prev = this.driver;
    this.driver = driver;
    if (!this._ready) {
      this._ready = true;
      eventBus.emit({ type: "render:coordinator_ready" });
      logger.info("RenderCoordinator ready");
    }
    return prev;
  }

  /** Remove the current driver. No new frames will execute until one is registered. */
  removeDriver(): void {
    this.driver = null;
    this._ready = false;
  }

  // ── Frame lifecycle ──────────────────────────────────────────────────────────

  /**
   * Request a render frame for a page.
   * If a pending or running frame exists for this page, the pending one is
   * cancelled and a new one queued. Returns the new frame ID.
   */
  requestFrame(pageId: string): string {
    // Cancel any existing pending frame for this page
    const existingFrameId = this.active.get(pageId);
    if (existingFrameId) {
      const existing = this.frames.get(existingFrameId);
      if (existing && existing.status === "pending") {
        this._cancel(existing, "superseded_by_new_request");
      }
    }

    if (this.active.size >= MAX_CONCURRENT) {
      logger.warn("RenderCoordinator: concurrent frame limit reached", { pageId, limit: MAX_CONCURRENT });
    }

    const frame: RenderFrame = {
      frameId:     crypto.randomUUID(),
      pageId,
      requestedAt: Date.now(),
      status:      "pending",
    };

    this.frames.set(frame.frameId, frame);
    this.active.set(pageId, frame.frameId);
    this._total += 1;

    eventBus.emit({ type: "render:frame_started", frameId: frame.frameId, pageId });

    // Debounce: give the caller a tick to batch multiple requests
    const timer = setTimeout(() => {
      this.timers.delete(frame.frameId);
      if (frame.status === "pending") {
        this._runFrame(frame);
      }
    }, DEBOUNCE_MS);
    this.timers.set(frame.frameId, timer);

    return frame.frameId;
  }

  /**
   * Signal that a frame has completed successfully.
   * Should be called by the RenderDriver when rendering finishes.
   */
  completeFrame(frameId: string): void {
    const frame = this.frames.get(frameId);
    if (!frame || frame.status !== "running") return;

    const durationMs = Date.now() - (frame.startedAt ?? frame.requestedAt);
    frame.status      = "completed";
    frame.completedAt = Date.now();
    frame.durationMs  = durationMs;

    this._completed += 1;
    this._durationsMs.push(durationMs);
    if (this._durationsMs.length > 200) this._durationsMs.shift();

    this._clearTimeout(frameId);
    this.active.delete(frame.pageId);
    this._archiveFrame(frame);

    eventBus.emit({ type: "render:frame_completed", frameId, pageId: frame.pageId, durationMs });
    logger.debug("RenderCoordinator: frame completed", { frameId, pageId: frame.pageId, durationMs });
  }

  /**
   * Signal that a frame failed.
   * Should be called by the RenderDriver on error.
   */
  failFrame(frameId: string, error: string): void {
    const frame = this.frames.get(frameId);
    if (!frame || (frame.status !== "running" && frame.status !== "pending")) return;

    frame.status  = "failed";
    frame.error   = error;
    frame.durationMs = Date.now() - (frame.startedAt ?? frame.requestedAt);

    this._failed += 1;
    this._clearTimeout(frameId);
    this.active.delete(frame.pageId);
    this._archiveFrame(frame);

    eventBus.emit({ type: "render:frame_failed", frameId, pageId: frame.pageId, error });
    logger.warn("RenderCoordinator: frame failed", { frameId, pageId: frame.pageId, error });
  }

  // ── Read API ─────────────────────────────────────────────────────────────────

  /** Get a frame descriptor by ID. */
  getFrame(frameId: string): RenderFrame | null {
    return this.frames.get(frameId) ?? null;
  }

  /** Get recent frame history, newest first. */
  getHistory(limit = 50): RenderFrame[] {
    return this.history.slice(-limit).reverse();
  }

  /** Coordinator statistics for diagnostics. */
  getStats(): RenderCoordinatorStats {
    const avg = this._durationsMs.length > 0
      ? Math.round(this._durationsMs.reduce((a, b) => a + b, 0) / this._durationsMs.length)
      : 0;
    return {
      totalFrames:     this._total,
      completedFrames: this._completed,
      failedFrames:    this._failed,
      cancelledFrames: this._cancelled,
      activeFrames:    this.active.size,
      avgDurationMs:   avg,
      isReady:         this._ready,
    };
  }

  /** True if a driver is registered and ready. */
  isReady(): boolean { return this._ready; }

  // ── Internal ─────────────────────────────────────────────────────────────────

  private _runFrame(frame: RenderFrame): void {
    if (!this.driver) {
      this.failFrame(frame.frameId, "No render driver registered");
      return;
    }

    frame.status    = "running";
    frame.startedAt = Date.now();

    // Timeout guard
    const timer = setTimeout(() => {
      if (frame.status === "running") {
        this.failFrame(frame.frameId, `Frame timed out after ${MAX_FRAME_MS}ms`);
      }
    }, MAX_FRAME_MS);
    this.timers.set(frame.frameId, timer);

    try {
      const result = this.driver(frame);
      if (result instanceof Promise) {
        result
          .then(() => this.completeFrame(frame.frameId))
          .catch((err: unknown) => {
            const msg = err instanceof Error ? err.message : String(err);
            this.failFrame(frame.frameId, msg);
          });
      } else {
        this.completeFrame(frame.frameId);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.failFrame(frame.frameId, msg);
    }
  }

  private _cancel(frame: RenderFrame, _reason: string): void {
    frame.status = "cancelled";
    this._cancelled += 1;
    this._clearTimeout(frame.frameId);
    this.active.delete(frame.pageId);
    this._archiveFrame(frame);
    logger.debug("RenderCoordinator: frame cancelled", { frameId: frame.frameId, pageId: frame.pageId });
  }

  private _clearTimeout(frameId: string): void {
    const t = this.timers.get(frameId);
    if (t !== undefined) {
      clearTimeout(t);
      this.timers.delete(frameId);
    }
  }

  private _archiveFrame(frame: RenderFrame): void {
    this.frames.delete(frame.frameId);
    this.history.push({ ...frame });
    if (this.history.length > MAX_HISTORY) {
      this.history = this.history.slice(-MAX_HISTORY);
    }
  }
}

export const renderCoordinator = new RenderCoordinatorImpl();
